import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Users, Shield, Plus, Trash2, Edit3, Check, X, Loader2, Save,
  Lock, Mail, UserPlus, ScrollText, AlertCircle, Tag, RefreshCw, Smartphone,
  Upload, Database, FileSpreadsheet, Bell, Eye, EyeOff, Copy, KeyRound,
  FileText, ImagePlus, Palette, HardDrive, MessageCircle, BookOpen, Activity, Layers,
} from "lucide-react";
import { toast } from "sonner";
import BackupTab from "@/pages/settings/BackupTab";
import WhatsAppTab from "@/pages/settings/WhatsAppTab";
import BizimHesapTab from "@/pages/settings/BizimHesapTab";
import SystemHealthTab from "@/pages/settings/SystemHealthTab";
import SmtpTab from "@/pages/settings/SmtpTab";
import PricingTab from "@/pages/settings/PricingTab";
import ServicesTab from "@/pages/settings/ServicesTab";
import LearningAdminTab from "@/pages/settings/LearningAdminTab";
import SaasAdminTab from "@/pages/settings/SaasAdminTab";
import AuditTab from "@/pages/settings/AuditTab";

const ACTION_LABELS = {
  view: "Görüntüle",
  add: "Ekle",
  edit: "Düzenle",
  delete: "Sil",
  print: "Yazdır",
  export: "Dışa Aktar",
  approve: "Onayla",
};

// Ayarlar sekmeleri kategori bazlı gruplandırılmış. Yeni sekme eklendiğinde
// yalnızca bu listeye satır eklenir — TabsList / dropdown otomatik günceller.
const SETTINGS_GROUPS = [
  {
    key: "genel",
    title: "Genel",
    items: [
      { value: "users",       label: "Kullanıcılar",      icon: Users },
      { value: "roles",       label: "Roller & İzinler",  icon: Shield },
      { value: "definitions", label: "Tanımlar",          icon: Tag },
      { value: "uygulamalar", label: "Uygulamalar",       icon: Layers },
      { value: "fiyatlandirma", label: "Fiyatlandırma",   icon: Tag },
      { value: "app",         label: "Uygulama",          icon: Smartphone },
    ],
  },
  {
    key: "sistem",
    title: "Sistem",
    items: [
      { value: "bildirim",  label: "Bildirimler",     icon: Bell },
      { value: "sablonlar", label: "Belge Şablonları", icon: FileText },
      { value: "yedekleme", label: "Yedekleme",       icon: HardDrive },
      { value: "saglik",    label: "Sistem Sağlığı",  icon: Activity },
      { value: "audit",     label: "İşlem Geçmişi",   icon: ScrollText },
    ],
  },
  {
    key: "entegrasyon",
    title: "Entegrasyonlar",
    items: [
      { value: "whatsapp",    label: "WhatsApp",             icon: MessageCircle },
      { value: "bizim-hesap", label: "Bizim Hesap",          icon: BookOpen },
      { value: "gib",         label: "GİB Mükellef Listesi", icon: Database },
      { value: "smtp",        label: "SMTP (E-posta)",       icon: Mail },
    ],
  },
  {
    key: "yardim",
    title: "Yardım",
    items: [
      { value: "egitim", label: "Eğitim & Yardım", icon: BookOpen },
    ],
  },
  {
    key: "platform",
    title: "Platform",
    items: [
      { value: "saas", label: "SaaS / Krediler", icon: KeyRound },
    ],
  },
];
const ALL_TABS = SETTINGS_GROUPS.flatMap((g) => g.items);
const VALID_TAB_VALUES = ALL_TABS.map((t) => t.value);

// Yalnız GERÇEK admin/super-admin seviyesi kalması gerekenler (permission matrisinde canonical
// karşılığı YOK — bkz. users_admin.MODULES). Diğer güvenli sekmeler settings.view/edit ile
// açılır; secret alanları backend response'unda zaten redakte (WhatsAppConfigPublic, smtp
// password_set gibi bayraklar).
const ADMIN_ONLY_TABS = new Set([
  "users", "roles", "audit", "saas", "saglik", "yedekleme", "egitim",
]);

export default function Settings() {
  const { user, perms, can } = useAuth();
  const isAdmin = !!(perms?.is_admin || user?.is_super_admin || user?.role === "admin");
  const canAudit = isAdmin || can("audit", "view");
  const canSettingsView = isAdmin || can("settings", "view");
  const canViewSettings = canSettingsView || canAudit;
  const canEditSettings = isAdmin || can("settings", "edit");
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = VALID_TAB_VALUES.includes(searchParams.get("tab")) ? searchParams.get("tab") : "users";
  const [tab, setTab] = useState(initialTab);
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && VALID_TAB_VALUES.includes(t) && t !== tab) setTab(t);
  }, [searchParams]); // eslint-disable-line
  const changeTab = (t) => {
    setTab(t);
    const next = new URLSearchParams(searchParams);
    next.set("tab", t);
    setSearchParams(next, { replace: true });
  };
  const visibleGroups = SETTINGS_GROUPS
    .map((g) => ({ ...g, items: g.items.filter((it) => (it.value === "audit" ? canAudit : (isAdmin || (canSettingsView && !ADMIN_ONLY_TABS.has(it.value))))) }))
    .filter((g) => g.items.length > 0);
  const visibleValues = new Set(visibleGroups.flatMap((g) => g.items.map((i) => i.value)));
  const effectiveTab = visibleValues.has(tab) ? tab : (visibleGroups[0]?.items[0]?.value || "app");

  if (user && perms === null) {
    return <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 text-blue-500 animate-spin" /></div>;
  }
  if (!canViewSettings) {
    return (
      <div className="p-10 max-w-md mx-auto text-center space-y-2" data-testid="settings-no-access">
        <Shield className="w-8 h-8 mx-auto text-slate-600" />
        <div className="text-sm font-semibold">Bu sayfaya erişim yetkiniz yok</div>
        <div className="text-xs text-muted-foreground">Ayarlar görüntüleme yetkisi (settings.view) için yöneticinize başvurun.</div>
      </div>
    );
  }

  const activeItem = ALL_TABS.find((t) => t.value === effectiveTab) || ALL_TABS[0];
  const activeGroup = SETTINGS_GROUPS.find((g) => g.items.some((i) => i.value === effectiveTab));
  return (
    <div>
      <PageHeader
        title="Ayarlar"
        subtitle={isAdmin ? "Kullanıcılar, Roller, İzinler ve İşlem Geçmişi" : "Tanımlar, bildirimler ve belge ayarları"}
        breadcrumbs={["Ana Sayfa", "Ayarlar"]}
      />
      <div className="w-full px-3 sm:px-4 lg:px-6 py-4 lg:py-6">
        <Tabs value={effectiveTab} onValueChange={changeTab} data-testid="settings-tabs" className="w-full">
          {/* Mobil / tablet: kategori dropdown */}
          <div className="lg:hidden mb-4" data-testid="settings-mobile-nav">
            <Select value={tab} onValueChange={changeTab}>
              <SelectTrigger
                data-testid="settings-mobile-nav-trigger"
                className="w-full rounded-sm bg-muted/30 border-border h-11"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Tag className="w-4 h-4 shrink-0 text-blue-500" />
                  <span className="truncate font-semibold">Ayarlar Menüsü</span>
                  {activeItem?.label && (
                    <span className="ml-auto pl-2 text-[10px] uppercase font-mono text-muted-foreground shrink-0 truncate max-w-[45%]">
                      {activeItem.label}
                    </span>
                  )}
                </div>
              </SelectTrigger>
              <SelectContent className="max-h-[70vh]">
                {visibleGroups.map((g) => (
                  <SelectGroup key={g.key}>
                    <SelectLabel className="text-[10px] uppercase font-mono tracking-widest text-blue-500/80">
                      {g.title}
                    </SelectLabel>
                    {g.items.map((it) => (
                      <SelectItem key={it.value} value={it.value} data-testid={`settings-mobile-item-${it.value}`}>
                        <div className="flex items-center gap-2">
                          <it.icon className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
                          <span>{it.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Masaüstü: iki sütun (sidebar + içerik) */}
          <div className="flex flex-col lg:flex-row lg:gap-6 w-full min-w-0">
            <aside
              className="hidden lg:block lg:w-60 lg:shrink-0"
              data-testid="settings-desktop-nav"
            >
              <nav className="sticky top-4 space-y-4 pr-1">
                {visibleGroups.map((g) => (
                  <div key={g.key}>
                    <div className="text-[10px] uppercase font-mono tracking-widest text-muted-foreground px-2 mb-1.5">
                      {g.title}
                    </div>
                    <div className="space-y-0.5">
                      {g.items.map((it) => {
                        const Icon = it.icon;
                        const active = effectiveTab === it.value;
                        return (
                          <button
                            key={it.value}
                            type="button"
                            onClick={() => changeTab(it.value)}
                            data-testid={`tab-${it.value}`}
                            aria-current={active ? "page" : undefined}
                            className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-sm text-left text-sm transition-colors border-l-2
                              ${active
                                ? "bg-blue-500/10 text-blue-300 border-blue-500 font-semibold"
                                : "text-slate-300 border-transparent hover:bg-muted/40 hover:text-slate-100"}`}
                          >
                            <Icon className={`w-3.5 h-3.5 shrink-0 ${active ? "text-blue-400" : "text-muted-foreground"}`} />
                            <span className="truncate">{it.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </nav>
            </aside>

            {/* İçerik alanı — flex-1 min-w-0 tüm kalan genişliği kullanır ve
                iç tablolar overflow'a düşerse yatay taşma yerine kart içinde
                scroll olur. */}
            <div className="flex-1 min-w-0 w-full">
              {/* Radix TabsList erişilebilirlik/klavye navigasyonu için
                  DOM'da kalmalı; görsel olarak gizliyoruz — asıl navigasyon
                  yukarıdaki sidebar/dropdown. */}
              <TabsList className="sr-only" aria-hidden="true">
                {ALL_TABS.map((t) => (
                  <TabsTrigger key={t.value} value={t.value} />
                ))}
              </TabsList>

              <TabsContent value="users" className="mt-0"><UsersTab /></TabsContent>
              <TabsContent value="roles" className="mt-0"><RolesTab /></TabsContent>
              <TabsContent value="definitions" className="mt-0"><DefinitionsTab canEdit={canEditSettings} /></TabsContent>
              <TabsContent value="uygulamalar" className="mt-0"><ServicesTab canEdit={canEditSettings} /></TabsContent>
              <TabsContent value="fiyatlandirma" className="mt-0"><PricingTab canEdit={canEditSettings} /></TabsContent>
              <TabsContent value="audit" className="mt-0"><AuditTab /></TabsContent>
              <TabsContent value="app" className="mt-0"><AppTab /></TabsContent>
              <TabsContent value="gib" className="mt-0"><GibTaxpayerTab canEdit={canEditSettings} /></TabsContent>
              <TabsContent value="bildirim" className="mt-0"><NotificationsTab /></TabsContent>
              <TabsContent value="sablonlar" className="mt-0"><DocumentTemplatesTab canEdit={canEditSettings} /></TabsContent>
              <TabsContent value="yedekleme" className="mt-0"><BackupTab /></TabsContent>
              <TabsContent value="whatsapp" className="mt-0"><WhatsAppTab /></TabsContent>
              <TabsContent value="bizim-hesap" className="mt-0"><BizimHesapTab /></TabsContent>
              <TabsContent value="saglik" className="mt-0"><SystemHealthTab /></TabsContent>
              <TabsContent value="smtp" className="mt-0"><SmtpTab /></TabsContent>
              <TabsContent value="egitim" className="mt-0"><LearningAdminTab /></TabsContent>
              <TabsContent value="saas" className="mt-0"><SaasAdminTab /></TabsContent>
            </div>
          </div>
        </Tabs>
      </div>
    </div>
  );
}

// ---- Users tab ------------------------------------------------------------
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null); // null = closed, {} = new, obj = edit
  const [tempPwInfo, setTempPwInfo] = useState(null); // { email, temp_password, mode: 'create'|'reset' }
  const [resetTarget, setResetTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [u, r, m] = await Promise.all([
        api.get("/users"), api.get("/roles"), api.get("/meta/modules"),
      ]);
      setUsers(u.data || []);
      setRoles(r.data || []);
      setModules(m.data?.modules || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (u) => {
    if (!window.confirm(`${u.email} kullanıcısı silinsin mi?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Silindi");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resetPassword = async (u) => {
    setResetTarget(u.id);
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`);
      setTempPwInfo({
        email: data.email,
        temp_password: data.temp_password,
        mode: "reset",
      });
      toast.success(`${u.email} için yeni geçici şifre üretildi`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setResetTarget(null); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase font-mono text-muted-foreground">
          {users.length} kullanıcı
        </div>
        <Button
          data-testid="user-add-btn"
          onClick={() => setEditUser({})}
          className="rounded-sm bg-primary hover:bg-primary/90"
        >
          <UserPlus className="w-4 h-4 mr-1" /> Kullanıcı Ekle
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
        </div>
      ) : (
        <div className="border border-border rounded-sm overflow-hidden">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="bg-muted/40 border-b border-border">
              <tr className="text-[10px] uppercase font-mono text-muted-foreground">
                <th className="text-left px-3 py-2">Ad Soyad</th>
                <th className="text-left px-3 py-2">Email</th>
                <th className="text-left px-3 py-2">Rol</th>
                <th className="text-center px-3 py-2">Durum</th>
                <th className="text-right px-3 py-2">Oluşturuldu</th>
                <th className="px-3 py-2 w-24">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td className="px-3 py-2 font-semibold">{u.name}</td>
                  <td className="px-3 py-2 text-xs font-mono">{u.email}</td>
                  <td className="px-3 py-2">
                    {u.role_name ? (
                      <span className="text-xs bg-primary/10 text-primary border border-primary/40 rounded-sm px-2 py-0.5">
                        {u.role_name}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">— rol yok —</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {u.is_active ? (
                      <span className="inline-flex items-center gap-1 text-emerald-500 text-xs">
                        <Check className="w-3 h-3" /> Aktif
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-rose-500 text-xs">
                        <X className="w-3 h-3" /> Pasif
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right text-xs font-mono text-muted-foreground">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString("tr-TR") : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        data-testid={`user-edit-${u.id}`}
                        onClick={() => setEditUser(u)}
                        className="text-muted-foreground hover:text-amber-500 p-1"
                        title="Düzenle"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        data-testid={`user-reset-pw-${u.id}`}
                        onClick={() => {
                          if (!window.confirm(`${u.email} için yeni geçici şifre oluşturulsun mu? Eski şifresi geçersiz olacak.`)) return;
                          resetPassword(u);
                        }}
                        disabled={resetTarget === u.id}
                        className="text-muted-foreground hover:text-sky-500 p-1 disabled:opacity-50"
                        title="Şifre Sıfırla"
                      >
                        {resetTarget === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
                      </button>
                      <button
                        data-testid={`user-delete-${u.id}`}
                        onClick={() => remove(u)}
                        className="text-muted-foreground hover:text-rose-500 p-1"
                        title="Sil"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-sm text-muted-foreground">
                  Kullanıcı yok. "Kullanıcı Ekle" ile başlayın.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <UserDialog
        user={editUser}
        open={!!editUser}
        onOpenChange={(v) => !v && setEditUser(null)}
        roles={roles}
        modules={modules}
        onSaved={load}
        onTempPassword={(info) => setTempPwInfo(info)}
      />

      <TempPasswordDialog
        info={tempPwInfo}
        open={!!tempPwInfo}
        onOpenChange={(v) => !v && setTempPwInfo(null)}
      />
    </div>
  );
}

// ---- Temp Password Modal --------------------------------------------------
function TempPasswordDialog({ info, open, onOpenChange }) {
  const [show, setShow] = useState(false);
  useEffect(() => { if (!open) setShow(false); }, [open]);
  if (!info) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(info.temp_password);
      toast.success("Şifre kopyalandı");
    } catch {
      toast.error("Kopyalanamadı — manuel seçin");
    }
  };

  const title = info.mode === "reset" ? "Şifre Sıfırlandı" : "Kullanıcı Oluşturuldu — Geçici Şifre";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-md" data-testid="temp-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-blue-400" /> {title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="p-3 rounded-sm border border-amber-800/60 bg-amber-950/30 text-amber-200 text-xs">
            <AlertCircle className="w-4 h-4 inline mr-1" />
            Bu şifre <b>sadece bir kez</b> gösteriliyor. Lütfen kopyalayıp kullanıcıya
            güvenli bir kanaldan iletin. Kullanıcı ilk girişte kendi şifresini
            değiştirmesi için yönlendirilecek.
          </div>
          <div>
            <Label className="text-xs uppercase font-mono text-muted-foreground">E-posta</Label>
            <div className="text-sm font-mono mt-1 p-2 bg-muted/40 rounded-sm" data-testid="temp-pw-email">
              {info.email}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase font-mono text-muted-foreground">Geçici Şifre</Label>
            <div className="flex items-center gap-2 mt-1">
              <div
                className="flex-1 text-lg font-mono tracking-widest p-2 bg-slate-900/70 border border-slate-800 rounded-sm select-all"
                data-testid="temp-pw-value"
              >
                {show ? info.temp_password : "•".repeat(info.temp_password.length)}
              </div>
              <button
                data-testid="temp-pw-toggle"
                onClick={() => setShow((v) => !v)}
                className="p-2 rounded-sm border border-slate-800 hover:bg-slate-900 text-slate-300"
                title={show ? "Gizle" : "Göster"}
              >
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              <button
                data-testid="temp-pw-copy"
                onClick={copy}
                className="p-2 rounded-sm border border-slate-800 hover:bg-blue-950/40 hover:border-blue-800 text-blue-400"
                title="Kopyala"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            data-testid="temp-pw-close"
            onClick={() => onOpenChange(false)}
            className="rounded-sm"
          >
            Anladım, kapat
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UserDialog({ user, open, onOpenChange, roles, modules, onSaved, onTempPassword }) {
  const isNew = user && !user.id;
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [autoGenerate, setAutoGenerate] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [roleId, setRoleId] = useState("");
  const [customPerms, setCustomPerms] = useState({});
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCustom, setShowCustom] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    setEmail(user.email || "");
    setName(user.name || "");
    setPassword("");
    setAutoGenerate(!!isNew); // yeni kullanıcıda varsayılan otomatik; düzenlemede kapalı
    setShowPassword(false);
    setRoleId(user.role_id || "");
    setCustomPerms(user.custom_permissions || {});
    setIsActive(user.is_active !== false);
    setShowCustom(Object.keys(user.custom_permissions || {}).length > 0);
  }, [open, user, isNew]);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        email, name, role_id: roleId || null,
        custom_permissions: customPerms, is_active: isActive,
      };
      // Yeni kullanıcı + autoGenerate → password göndermeyiz (backend üretecek)
      // Yeni kullanıcı + manuel → password göndeririz
      // Düzenleme + password dolu → password göndeririz (değişecek)
      if (isNew) {
        if (!autoGenerate) {
          if (!password || password.length < 6) {
            toast.error("Manuel şifre en az 6 karakter olmalı");
            setSaving(false); return;
          }
          body.password = password;
        }
        const { data } = await api.post("/users", body);
        toast.success("Kullanıcı eklendi");
        if (data?.temp_password) {
          onTempPassword?.({
            email: data.email,
            temp_password: data.temp_password,
            mode: "create",
          });
        }
      } else {
        if (password) body.password = password;
        await api.patch(`/users/${user.id}`, body);
        toast.success("Kullanıcı güncellendi");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const selectedRole = roles.find((r) => r.id === roleId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-3xl" data-testid="user-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "Yeni Kullanıcı" : `Kullanıcıyı Düzenle · ${user?.email}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Ad Soyad *</Label>
              <Input data-testid="user-name" value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1"><Mail className="w-3 h-3" /> Email *</Label>
              <Input data-testid="user-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-sm mt-1" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs flex items-center gap-1">
                <Lock className="w-3 h-3" /> Şifre {isNew ? "" : "(değiştirmek istersen doldur)"}
              </Label>
              {isNew && (
                <label className="flex items-center gap-2 mt-1.5 mb-1 text-xs cursor-pointer" data-testid="user-auto-gen-label">
                  <input
                    type="checkbox"
                    data-testid="user-auto-generate"
                    checked={autoGenerate}
                    onChange={(e) => setAutoGenerate(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className={autoGenerate ? "text-blue-400 font-semibold" : "text-slate-400"}>
                    Otomatik 10 karakterli geçici şifre üret
                  </span>
                </label>
              )}
              {(!isNew || !autoGenerate) && (
                <div className="flex items-center gap-1 mt-1">
                  <Input
                    data-testid="user-password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="rounded-sm flex-1"
                    placeholder={isNew ? "En az 6 karakter" : "değişmiyor"}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    data-testid="user-password-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    className="p-2 rounded-sm border border-slate-800 hover:bg-slate-900 text-slate-300"
                    title={showPassword ? "Gizle" : "Göster"}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              )}
              {isNew && autoGenerate && (
                <div className="text-[10px] text-slate-500 font-mono uppercase tracking-widest mt-1">
                  Kaydedince şifre bir kere gösterilecek
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs">Rol</Label>
              <Select value={roleId || "none"} onValueChange={(v) => setRoleId(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="user-role" className="rounded-sm mt-1"><SelectValue placeholder="Rol seç" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— rol yok —</SelectItem>
                  {roles.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.name}{r.is_preset && " (preset)"}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-2 border border-border rounded-sm p-3 bg-muted/30">
            <input
              type="checkbox" id="user-active"
              data-testid="user-active"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="w-4 h-4"
            />
            <label htmlFor="user-active" className="text-sm cursor-pointer">
              Kullanıcı <span className="font-bold">{isActive ? "aktif" : "pasif"}</span> (pasif kullanıcılar giriş yapamaz)
            </label>
          </div>

          {/* Role permissions preview */}
          {selectedRole && (
            <div className="border border-border rounded-sm p-3 bg-muted/20">
              <div className="text-xs uppercase font-mono text-muted-foreground mb-2">
                Rol İzinleri: {selectedRole.name}
              </div>
              <PermissionsMatrix
                modules={modules}
                value={selectedRole.permissions}
                readonly
              />
            </div>
          )}

          {/* Custom permissions toggle */}
          <div>
            <label className="text-xs flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="user-custom-toggle"
                checked={showCustom}
                onChange={(e) => setShowCustom(e.target.checked)}
                className="w-4 h-4"
              />
              <span className="font-semibold">Kullanıcı bazlı özel izin ekle (rol üstüne)</span>
            </label>
            {showCustom && (
              <div className="mt-2 border border-amber-500/40 rounded-sm p-3 bg-amber-500/5">
                <div className="text-xs uppercase font-mono text-amber-500 mb-2 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> Bu izinler rol izinlerine EKLENİR (birlik).
                </div>
                <PermissionsMatrix
                  modules={modules}
                  value={customPerms}
                  onChange={setCustomPerms}
                />
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-sm">İptal</Button>
          <Button data-testid="user-save-btn" onClick={save} disabled={saving} className="rounded-sm">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            {isNew ? "Ekle" : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Permissions Matrix ---------------------------------------------------
const ACTIONS = ["view", "add", "edit", "delete", "print", "export", "approve"];

function PermissionsMatrix({ modules, value, onChange, readonly }) {
  const has = (mod, act) => (value?.[mod] || []).includes(act);
  const toggle = (mod, act) => {
    if (readonly || !onChange) return;
    const list = new Set(value?.[mod] || []);
    if (list.has(act)) list.delete(act); else list.add(act);
    onChange({ ...value, [mod]: [...list] });
  };
  const toggleModuleAll = (mod, allOn) => {
    if (readonly || !onChange) return;
    onChange({ ...value, [mod]: allOn ? [] : [...ACTIONS] });
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="permission-matrix">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left px-2 py-1.5 font-mono uppercase text-[9px] text-muted-foreground">Modül</th>
            {ACTIONS.map((a) => (
              <th key={a} className="text-center px-1 py-1.5 font-mono uppercase text-[9px] text-muted-foreground w-14">
                {ACTION_LABELS[a] || a}
              </th>
            ))}
            {!readonly && <th className="text-center px-1 py-1.5 font-mono uppercase text-[9px] w-14">Tümü</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {modules.map((m) => {
            const activeCount = (value?.[m.code] || []).length;
            const allOn = activeCount === ACTIONS.length;
            return (
              <tr key={m.code} className={activeCount > 0 ? "bg-primary/5" : ""}>
                <td className="px-2 py-1 font-semibold text-xs">{m.label}</td>
                {ACTIONS.map((a) => (
                  <td key={a} className="text-center">
                    <input
                      type="checkbox"
                      data-testid={`perm-${m.code}-${a}`}
                      disabled={readonly}
                      checked={has(m.code, a)}
                      onChange={() => toggle(m.code, a)}
                      className="w-3.5 h-3.5 cursor-pointer accent-primary"
                    />
                  </td>
                ))}
                {!readonly && (
                  <td className="text-center">
                    <button
                      onClick={() => toggleModuleAll(m.code, allOn)}
                      className="text-[10px] text-primary hover:opacity-80 uppercase font-mono"
                    >
                      {allOn ? "Kapat" : "Aç"}
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---- Roles tab ------------------------------------------------------------
function RolesTab() {
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editRole, setEditRole] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, m] = await Promise.all([api.get("/roles"), api.get("/meta/modules")]);
      setRoles(r.data || []);
      setModules(m.data?.modules || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (r) => {
    if (r.is_preset) { toast.error("Preset rol silinemez"); return; }
    if (!window.confirm(`"${r.name}" rolü silinsin mi?`)) return;
    try {
      await api.delete(`/roles/${r.id}`);
      toast.success("Silindi");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase font-mono text-muted-foreground">
          {roles.length} rol ({roles.filter((r) => r.is_preset).length} preset)
        </div>
        <Button
          data-testid="role-add-btn"
          onClick={() => setEditRole({ name: "", permissions: {} })}
          className="rounded-sm"
        >
          <Plus className="w-4 h-4 mr-1" /> Yeni Rol
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {roles.map((r) => {
            const moduleCount = Object.keys(r.permissions || {}).filter((k) => (r.permissions[k] || []).length > 0).length;
            const actionCount = Object.values(r.permissions || {}).reduce((s, arr) => s + arr.length, 0);
            return (
              <Card
                key={r.id}
                data-testid={`role-card-${r.id}`}
                className="rounded-sm bg-card border-border p-4 space-y-2"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-bold">{r.name}</div>
                    <div className="text-[10px] font-mono text-muted-foreground">{r.code}</div>
                  </div>
                  {r.is_preset ? (
                    <span className="text-[10px] uppercase font-mono bg-blue-500/10 text-blue-500 border border-blue-500/40 rounded-sm px-1.5 py-0.5">
                      Preset
                    </span>
                  ) : (
                    <span className="text-[10px] uppercase font-mono bg-emerald-500/10 text-emerald-500 border border-emerald-500/40 rounded-sm px-1.5 py-0.5">
                      Özel
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground font-mono">
                  {moduleCount} modül · {actionCount} izin
                </div>
                <div className="flex items-center gap-1.5 pt-2 border-t border-border">
                  <Button
                    data-testid={`role-edit-${r.id}`}
                    variant="outline" size="sm"
                    onClick={() => setEditRole(r)}
                    className="rounded-sm text-xs h-7 flex-1"
                  >
                    <Edit3 className="w-3 h-3 mr-1" /> {r.is_preset ? "İzinleri Gör" : "Düzenle"}
                  </Button>
                  {!r.is_preset && (
                    <button
                      data-testid={`role-delete-${r.id}`}
                      onClick={() => remove(r)}
                      className="text-muted-foreground hover:text-rose-500 p-1.5"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <RoleDialog
        role={editRole}
        open={!!editRole}
        onOpenChange={(v) => !v && setEditRole(null)}
        modules={modules}
        onSaved={load}
      />
    </div>
  );
}

function RoleDialog({ role, open, onOpenChange, modules, onSaved }) {
  const isNew = role && !role.id;
  const isPreset = role?.is_preset;
  const [name, setName] = useState("");
  const [perms, setPerms] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !role) return;
    setName(role.name || "");
    setPerms(role.permissions || {});
  }, [open, role]);

  const save = async () => {
    if (!name.trim()) { toast.error("İsim girin"); return; }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/roles", { name, permissions: perms });
        toast.success("Rol eklendi");
      } else {
        await api.patch(`/roles/${role.id}`, { name, permissions: perms });
        toast.success("Rol güncellendi");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-4xl" data-testid="role-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "Yeni Rol" : `${isPreset ? "Preset Rol" : "Rol"} · ${role?.name}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <div>
            <Label className="text-xs">Rol Adı *</Label>
            <Input
              data-testid="role-name"
              value={name} onChange={(e) => setName(e.target.value)}
              disabled={isPreset}
              className="rounded-sm mt-1"
            />
          </div>
          <div>
            <Label className="text-xs mb-2 block">İzin Matrisi</Label>
            <div className="border border-border rounded-sm p-3">
              <PermissionsMatrix modules={modules} value={perms} onChange={setPerms} readonly={isPreset} />
            </div>
          </div>
          {isPreset && (
            <div className="text-xs text-muted-foreground border border-blue-500/40 bg-blue-500/5 rounded-sm p-2">
              Bu preset roldür. Değişiklik yapmak için "Yeni Rol" ile bir kopyasını oluşturun.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-sm">Kapat</Button>
          {!isPreset && (
            <Button data-testid="role-save-btn" onClick={save} disabled={saving} className="rounded-sm">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              {isNew ? "Ekle" : "Kaydet"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
//   Definitions Tab (Tanımlar): categories, brands, fabrics, accessories
// ============================================================
const DEF_TYPES = [
  { key: "product_category", label: "Ürün Kategorileri", hint: "Tişört, Pantolon, Yelek, Polar, Mont vb." },
  { key: "brand", label: "Markalar", hint: "Fucan, Fucan Pro vb." },
  { key: "fabric_type", label: "Kumaş Çeşitleri", hint: "Ripstop, Softshell, Polar, Pike vb." },
  { key: "fabric_name", label: "Kumaş Adları", hint: "Yağmurluk PU, Alev Almaz Aramid, Antistatik vb." },
  { key: "composition", label: "Kompozisyonlar", hint: "%100 Pamuk, %65 Poly %35 Pamuk, Aramid %100 vb." },
  { key: "color", label: "Renkler", hint: "Lacivert, Siyah, Beyaz, Haki, Fluoresan Turuncu vb." },
  { key: "accessory_category", label: "Aksesuar Çeşitleri", hint: "Fermuar, Düğme, Etiket, İplik vb." },
  { key: "accessory_name", label: "Aksesuar Adları", hint: "YKK Metal Fermuar 70cm, Reflektör Bant, Cırt Bant vb." },
];

function DefinitionsTab({ canEdit = true }) {
  const [activeKind, setActiveKind] = useState(DEF_TYPES[0].key);
  return (
    <Card className="bg-slate-900 border-slate-800 rounded-sm p-5" data-testid="definitions-tab">
      <div className="flex flex-wrap gap-2 mb-4">
        {DEF_TYPES.map((t) => (
          <button
            key={t.key}
            data-testid={`def-tab-${t.key}`}
            onClick={() => setActiveKind(t.key)}
            className={`text-xs font-mono uppercase tracking-widest px-3 py-1.5 rounded-sm border transition-colors ${
              activeKind === t.key
                ? "border-blue-500 bg-blue-950/40 text-blue-300"
                : "border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <DefinitionsList kind={activeKind} label={DEF_TYPES.find((t) => t.key === activeKind).label}
        hint={DEF_TYPES.find((t) => t.key === activeKind).hint} canEdit={canEdit} />
    </Card>
  );
}

function DefinitionsList({ kind, label, hint, canEdit = true }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/definitions/${kind}`);
      setItems(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const add = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await api.post(`/definitions/${kind}`, { name: newName.trim() });
      toast.success(`${newName.trim()} eklendi`);
      setNewName("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const remove = async (item) => {
    if (!window.confirm(`"${item.name}" silinsin mi?`)) return;
    try {
      await api.delete(`/definitions/${kind}/${item.id}`);
      toast.success("Silindi");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveEdit = async () => {
    if (!editing?.name?.trim()) return;
    try {
      await api.patch(`/definitions/${kind}/${editing.id}`, { name: editing.name.trim() });
      toast.success("Güncellendi");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleActive = async (item) => {
    try {
      await api.patch(`/definitions/${kind}/${item.id}`, { is_active: !item.is_active });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-1">{label}</div>
        <div className="text-[11px] text-slate-500 mb-3">{hint}</div>
      </div>
      {canEdit && (
      <div className="flex gap-2">
        <Input
          data-testid={`def-new-${kind}-input`}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") add(); }}
          placeholder={`Yeni ${label.toLowerCase()} ekle...`}
          className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 flex-1"
        />
        <Button
          data-testid={`def-add-${kind}-btn`}
          onClick={add}
          disabled={saving || !newName.trim()}
          className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Ekle</>}
        </Button>
      </div>
      )}
      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 text-blue-500 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-8 text-sm text-slate-500 border border-dashed border-slate-800 rounded-sm">
          Henüz kayıt yok. Yukarıdan ilkini ekleyin.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {items.map((it) => (
            <div
              key={it.id}
              data-testid={`def-item-${it.id}`}
              className={`group flex items-center gap-2 px-3 py-2 rounded-sm border ${
                it.is_active ? "border-slate-700 bg-slate-950" : "border-slate-800 bg-slate-950/40 opacity-60"
              }`}
            >
              {editing?.id === it.id ? (
                <>
                  <Input
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") setEditing(null); }}
                    autoFocus
                    className="bg-slate-900 border-slate-700 rounded-sm h-8 text-sm flex-1"
                  />
                  <button onClick={saveEdit} className="text-emerald-400 hover:text-emerald-300 p-1"><Check className="w-4 h-4" /></button>
                  <button onClick={() => setEditing(null)} className="text-slate-500 hover:text-slate-300 p-1"><X className="w-4 h-4" /></button>
                </>
              ) : (
                <>
                  <span className={`flex-1 text-sm font-mono ${it.is_active ? "text-slate-200" : "text-slate-500 line-through"}`}>{it.name}</span>
                  {canEdit && (
                    <>
                      <button onClick={() => toggleActive(it)} title={it.is_active ? "Pasif yap" : "Aktif yap"} className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-blue-400 p-1 transition-opacity">
                        {it.is_active ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                      </button>
                      <button data-testid={`def-edit-${it.id}`} onClick={() => setEditing({ id: it.id, name: it.name })} className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-blue-400 p-1 transition-opacity"><Edit3 className="w-3.5 h-3.5" /></button>
                      <button data-testid={`def-delete-${it.id}`} onClick={() => remove(it)} className="opacity-0 group-hover:opacity-100 text-rose-500 hover:text-rose-400 p-1 transition-opacity"><Trash2 className="w-3.5 h-3.5" /></button>
                    </>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ---- App / PWA cache tab --------------------------------------------------
function AppTab() {
  const [busy, setBusy] = useState(false);
  const [swInfo, setSwInfo] = useState({ registered: false, controller: null, waiting: false });

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    let mounted = true;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (!mounted) return;
        setSwInfo({
          registered: !!reg,
          controller: navigator.serviceWorker.controller?.scriptURL || null,
          waiting: !!reg?.waiting,
        });
      } catch (_) {}
    })();
    return () => { mounted = false; };
  }, []);

  const hardRefresh = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // 1) Tüm Cache Storage'ı temizle
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      // 2) SW kayıtlarını sıfırla
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      toast.success("Önbellek temizlendi — sayfa yenileniyor…");
      // 3) Küçük bir gecikmeyle sert yenileme (query ile cache-buster)
      setTimeout(() => {
        const sep = window.location.href.includes("?") ? "&" : "?";
        window.location.href = window.location.href.split("#")[0] + sep + "v=" + Date.now();
      }, 700);
    } catch (e) {
      toast.error("Temizleme başarısız — sayfayı elle yenileyin");
      setBusy(false);
    }
  };

  const softRefresh = () => {
    // SW'ye SKIP_WAITING yolla, sonra reload — controllerchange auto-reload
    // yakalamazsa manuel reload devreye girer.
    if (navigator.serviceWorker?.controller) {
      try { navigator.serviceWorker.controller.postMessage({ type: "SKIP_WAITING" }); } catch (_) {}
    }
    setTimeout(() => window.location.reload(), 300);
  };

  return (
    <Card className="rounded-sm border-border p-5 space-y-4 max-w-2xl">
      <div className="flex items-center gap-2 pb-3 border-b border-border">
        <Smartphone className="w-4 h-4 text-blue-500" />
        <h3 className="text-sm uppercase tracking-widest font-mono font-bold">
          Uygulama / Cache Yönetimi
        </h3>
      </div>

      <div className="text-sm text-muted-foreground space-y-2">
        <p>
          Telefon veya masaüstünde <b>eski görüntüler / eski veriler</b> görüyorsanız,
          tarayıcı ya da PWA'nın yerel önbelleği güncelleşememiş olabilir.
        </p>
        <p className="text-xs">
          Alttaki butonlar Service Worker cache'ini temizler ve uygulamayı en güncel sürümle yeniden yükler.
        </p>
      </div>

      <div className="border border-border rounded-sm bg-muted/20 p-3 text-xs font-mono space-y-1"
           data-testid="sw-status">
        <div>
          <span className="text-muted-foreground">Service Worker: </span>
          <span className={swInfo.registered ? "text-emerald-500" : "text-rose-500"}>
            {swInfo.registered ? "Kayıtlı ✓" : "Kayıt yok"}
          </span>
        </div>
        <div>
          <span className="text-muted-foreground">Kontrolde: </span>
          <span className="text-slate-300">
            {swInfo.controller ? new URL(swInfo.controller).pathname : "—"}
          </span>
        </div>
        {swInfo.waiting && (
          <div className="text-amber-500">⏳ Yeni sürüm bekliyor — "Uygulamayı Yenile" butonuna basın.</div>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          data-testid="soft-refresh-btn"
          size="sm"
          onClick={softRefresh}
          disabled={busy}
          className="rounded-sm bg-blue-600 hover:bg-blue-500"
        >
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Uygulamayı Yenile
        </Button>
        <Button
          data-testid="hard-refresh-btn"
          size="sm"
          variant="outline"
          onClick={hardRefresh}
          disabled={busy}
          className="rounded-sm border-rose-700 text-rose-400 hover:bg-rose-950/40"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}
          Önbelleği Temizle & Yenile
        </Button>
      </div>

      <div className="border-t border-border pt-3 text-[11px] text-muted-foreground space-y-1">
        <p className="font-semibold text-slate-300">📱 Telefon PWA'sını güncelleme adımları:</p>
        <ol className="list-decimal list-inside space-y-0.5">
          <li>Bu butonlardan birine tıklayın (Uygulamayı Yenile önerilir)</li>
          <li>İşe yaramazsa uygulamayı <b>tamamen kapatıp</b> (recent apps'ten kaydırma) yeniden açın</li>
          <li>Yine olmuyorsa Android: Ayarlar → Uygulamalar → ACUTO 360 → Depolama → Önbelleği Temizle</li>
          <li>iOS: Safari → Ayarlar → Web Sitesi Verilerini Sil</li>
        </ol>
      </div>
    </Card>
  );
}

// ---- GİB e-Fatura mükellef listesi paneli ---------------------------------
function GibTaxpayerTab({ canEdit = true }) {
  const [stats, setStats] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [purging, setPurging] = useState(false);
  const [testVkn, setTestVkn] = useState("");
  const [testResult, setTestResult] = useState(null);

  const loadStats = async () => {
    try { const { data } = await api.get("/tax-lookup/stats"); setStats(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadStats(); }, []);

  const uploadFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/tax-lookup/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000, // 2 dk
      });
      toast.success(`${data.processed} kayıt işlendi · Atlanan: ${data.skipped}`);
      loadStats();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const purge = async () => {
    if (!window.confirm("Tüm mükellef verisi silinsin mi? Yeniden import etmeniz gerekir.")) return;
    setPurging(true);
    try {
      const { data } = await api.delete("/tax-lookup/purge");
      toast.success(`${data.deleted} kayıt silindi`);
      loadStats();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPurging(false); }
  };

  const testLookup = async () => {
    const v = testVkn.trim();
    if (!v) return;
    setTestResult(null);
    try {
      const { data } = await api.get(`/tax-lookup/${v}`);
      setTestResult({ ok: true, ...data });
    } catch (e) {
      setTestResult({ ok: false, error: e?.response?.data?.detail || formatApiError(e) });
    }
  };

  return (
    <div className="space-y-5 max-w-3xl">
      <Card className="rounded-sm border-border p-5 space-y-4">
        <div className="flex items-center gap-2 pb-3 border-b border-border">
          <Database className="w-4 h-4 text-blue-400" />
          <h3 className="text-sm uppercase tracking-widest font-mono font-bold">
            GİB e-Fatura Mükellef Listesi
          </h3>
        </div>

        <div className="text-sm text-muted-foreground space-y-2">
          <p>
            GİB'in <b>her hafta yayınladığı e-Fatura Kayıtlı Kullanıcılar Listesi</b>'ni buradan yükleyerek
            VKN sorgu özelliğini aktifleştirebilirsiniz. Yükledikten sonra program, tüm müşteri/tedarikçi
            formlarında VKN girildiğinde firma adını otomatik olarak dolduracaktır.
          </p>
          <div className="text-xs bg-muted/30 p-3 rounded-sm space-y-1">
            <p className="font-semibold text-slate-300">📥 Nereden indirilir?</p>
            <p>
              <a
                href="https://ebelge.gib.gov.tr/duyurular/kayitlikullanicilar.aspx"
                target="_blank" rel="noreferrer"
                className="text-blue-500 hover:underline break-all"
              >
                https://ebelge.gib.gov.tr/duyurular/kayitlikullanicilar.aspx
              </a>
            </p>
            <p className="text-slate-500">
              Bu sayfadan güncel <b>ZIP</b> veya <b>CSV</b> dosyasını indirin, sonra aşağıdaki butona yükleyin.
            </p>
          </div>
        </div>

        <div className="border border-border rounded-sm bg-muted/20 p-3 space-y-1 text-xs font-mono"
             data-testid="gib-stats">
          <div>
            <span className="text-muted-foreground">Kayıtlı mükellef sayısı: </span>
            <span className="text-emerald-400 font-bold">{stats?.count?.toLocaleString("tr-TR") ?? "—"}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Son güncelleme: </span>
            <span className="text-slate-300">
              {stats?.last_imported_at ? new Date(stats.last_imported_at).toLocaleString("tr-TR") : "—"}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">Dosya: </span>
            <span className="text-slate-300">{stats?.last_import_source || "—"}</span>
          </div>
        </div>

        {canEdit && (
        <div className="flex flex-wrap gap-2">
          <label className={`inline-flex items-center gap-2 px-4 h-9 rounded-sm cursor-pointer text-xs font-semibold uppercase tracking-wider transition-colors ${uploading ? "bg-slate-700 text-slate-400" : "bg-blue-600 hover:bg-blue-500 text-white"}`}>
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            CSV / ZIP Yükle
            <input
              type="file"
              accept=".csv,.txt,.zip"
              className="hidden"
              onChange={uploadFile}
              disabled={uploading}
              data-testid="gib-upload-input"
            />
          </label>
          <Button
            size="sm"
            variant="outline"
            onClick={purge}
            disabled={purging || (stats?.count || 0) === 0}
            className="rounded-sm border-rose-700 text-rose-400 hover:bg-rose-950/40"
            data-testid="gib-purge-btn"
          >
            {purging ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}
            Tümünü Sil
          </Button>
        </div>
        )}
      </Card>

      <Card className="rounded-sm border-border p-5 space-y-3">
        <div className="flex items-center gap-2 pb-2 border-b border-border">
          <FileSpreadsheet className="w-4 h-4 text-emerald-400" />
          <h3 className="text-sm uppercase tracking-widest font-mono font-bold">
            Hızlı Sorgu Testi
          </h3>
        </div>
        <div className="flex gap-2">
          <Input
            value={testVkn}
            onChange={(e) => setTestVkn(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && testLookup()}
            placeholder="10 haneli VKN veya 11 haneli TCKN"
            className="rounded-sm font-mono"
            data-testid="gib-test-vkn"
          />
          <Button
            onClick={testLookup}
            disabled={!testVkn}
            className="rounded-sm bg-emerald-600 hover:bg-emerald-500"
            data-testid="gib-test-btn"
          >
            Sorgula
          </Button>
        </div>
        {testResult && testResult.ok && (
          <div className="border border-emerald-800 bg-emerald-950/30 rounded-sm p-3 text-sm font-mono" data-testid="gib-test-result-ok">
            <div className="text-emerald-400 font-bold">✓ Bulundu</div>
            <div><span className="text-muted-foreground">Ünvan: </span><b>{testResult.title}</b></div>
            {testResult.alias && <div><span className="text-muted-foreground">Alias: </span>{testResult.alias}</div>}
            {testResult.registered_at && <div><span className="text-muted-foreground">Kayıt: </span>{testResult.registered_at}</div>}
          </div>
        )}
        {testResult && !testResult.ok && (
          <div className="border border-amber-800 bg-amber-950/30 rounded-sm p-3 text-sm text-amber-300" data-testid="gib-test-result-none">
            ⚠ {testResult.error}
          </div>
        )}
      </Card>
    </div>
  );
}



// ---- NotificationsTab ------------------------------------------------------
const NOTIFICATION_KINDS = [
  { key: "task_assigned", label: "Yeni Görev Atandı" },
  { key: "task_reassigned", label: "Yeniden Atandı" },
  { key: "task_delegated", label: "Devir İsteği Geldi" },
  { key: "task_delegation_accepted", label: "Devir Kabul Edildi" },
  { key: "task_delegation_rejected", label: "Devir Reddedildi" },
  { key: "task_comment_added", label: "Yorum Yapıldı" },
  { key: "task_mentioned", label: "Yorumda Etiketlendim" },
  { key: "task_attachment_added", label: "Dosya Eklendi" },
  { key: "task_reminder_3d", label: "3 Gün Kala Hatırlatma" },
  { key: "task_reminder_1d", label: "1 Gün Kala Hatırlatma" },
  { key: "task_reminder_1h", label: "1 Saat Kala Hatırlatma" },
  { key: "task_overdue", label: "Görev Geciktiğinde" },
  { key: "task_completed", label: "Görev Tamamlandı" },
  { key: "task_status_changed", label: "Durum Değişti" },
  { key: "subtask_completed", label: "Alt Görev Tamamlandı" },
];

function NotificationsTab() {
  const [settings, setSettings] = useState({ email_enabled: false, kinds_muted: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/notifications/settings/me");
        setSettings({
          email_enabled: !!data.email_enabled,
          kinds_muted: data.kinds_muted || [],
        });
      } catch { /* silent */ } finally { setLoading(false); }
    })();
  }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/notifications/settings/me", next);
      setSettings({
        email_enabled: !!data.email_enabled,
        kinds_muted: data.kinds_muted || [],
      });
      toast.success("Bildirim tercihleri kaydedildi");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const toggleMute = (key) => {
    const next = settings.kinds_muted.includes(key)
      ? settings.kinds_muted.filter((k) => k !== key)
      : [...settings.kinds_muted, key];
    save({ kinds_muted: next });
  };

  if (loading) {
    return <div className="text-slate-500 text-sm p-6"><Loader2 className="w-4 h-4 inline mr-2 animate-spin" /> Yükleniyor…</div>;
  }

  return (
    <div className="space-y-4">
      <Card className="p-5 rounded-sm bg-card border-border" data-testid="notif-settings-email">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-sm font-bold text-slate-100 mb-1">E-posta Bildirimleri</div>
            <div className="text-xs text-slate-500">
              Bildirimler bell simgesinde her zaman görünür. E-posta gönderimi yalnızca
              Resend/SendGrid entegrasyonu yapıldığında aktif olur — şimdilik yalnızca
              tercih kaydedilir.
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer shrink-0">
            <input
              data-testid="notif-email-toggle"
              type="checkbox"
              checked={settings.email_enabled}
              disabled={saving}
              onChange={(e) => save({ email_enabled: e.target.checked })}
              className="w-4 h-4"
            />
            <span className="text-xs uppercase font-mono tracking-widest text-slate-300">
              {settings.email_enabled ? "Açık" : "Kapalı"}
            </span>
          </label>
        </div>
      </Card>

      <Card className="p-5 rounded-sm bg-card border-border" data-testid="notif-kinds-panel">
        <div className="text-sm font-bold text-slate-100 mb-2">Bildirim Türü Yönetimi</div>
        <div className="text-xs text-slate-500 mb-4">
          Aşağıdaki bildirim türlerini kapatabilirsiniz. Kapalı türler ne bell'e ne de
          e-postaya iletilir.
        </div>
        <div className="grid md:grid-cols-2 gap-2">
          {NOTIFICATION_KINDS.map((k) => {
            const muted = settings.kinds_muted.includes(k.key);
            return (
              <label
                key={k.key}
                data-testid={`notif-kind-${k.key}`}
                className={`flex items-center gap-3 p-2.5 rounded-sm border cursor-pointer transition-colors ${
                  muted
                    ? "border-slate-800 bg-slate-900/40 text-slate-500"
                    : "border-emerald-900/60 bg-emerald-950/20 text-slate-100"
                }`}
              >
                <input
                  type="checkbox"
                  checked={!muted}
                  disabled={saving}
                  onChange={() => toggleMute(k.key)}
                  className="w-4 h-4"
                />
                <span className="text-xs flex-1">{k.label}</span>
                {muted ? (
                  <span className="text-[10px] uppercase font-mono tracking-widest text-slate-600">Kapalı</span>
                ) : (
                  <span className="text-[10px] uppercase font-mono tracking-widest text-emerald-400">Açık</span>
                )}
              </label>
            );
          })}
        </div>
      </Card>

      {/* P3 — WhatsApp Bildirim Tercihleri (kullanıcı bazlı) */}
      <WhatsAppStaffPrefs />
    </div>
  );
}

// ---- P3: WhatsApp Bildirim Tercihleri (self) -------------------------------
// Backend: GET/PATCH /api/staff-notifications/prefs/{kind}/{rid}
// Kullanıcı yalnızca 8 iş-akışı event'ini görür (task/sample/purchase/shipment)
// — teknik event'ler (production_changed, pdks_missing_checkin, ...) gizlenir.
const USER_FACING_EVENTS = new Set([
  "task_assigned",
  "task_due_soon",
  "task_overdue",
  "task_completed",
  "sample_return_soon",
  "sample_overdue_staff",
  "purchase_delivery_soon",
  "purchase_overdue",
  "shipment_ready",
]);

// Varsayılanlar — kullanıcı ilk defa açtığında hangi event ON olsun
const DEFAULT_ON_EVENTS = new Set([
  "task_assigned", "task_overdue", "task_completed",
  "sample_overdue_staff", "purchase_overdue",
  "shipment_ready",
]);

function WhatsAppStaffPrefs() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/staff-notifications/prefs/self");
        setState(data);
      } catch { /* silent */ } finally { setLoading(false); }
    })();
  }, []);

  const persist = async (patch) => {
    if (!state) return;
    setSaving(true);
    try {
      const nextEvents = { ...(state.events || {}), ...(patch.events || {}) };
      const body = {
        phone: patch.phone !== undefined ? patch.phone : (state.phone || ""),
        enabled: patch.enabled !== undefined ? patch.enabled : !!state.enabled,
        events: nextEvents,
      };
      const { data } = await api.patch(`/staff-notifications/prefs/user/${state.user_id}`, body);
      // Re-fetch to include has_phone recomputed
      const { data: fresh } = await api.get("/staff-notifications/prefs/self");
      setState({ ...fresh, events: data.events || fresh.events });
      toast.success("WhatsApp tercihleri kaydedildi");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return null;
  if (!state) return null;

  const catalog = (state.catalog || []).filter((c) => USER_FACING_EVENTS.has(c.key));
  const events = state.events || {};
  const disabledByPhone = !state.has_phone;
  const masterOn = !!state.enabled && !disabledByPhone;

  const isOn = (k) => (k in events ? !!events[k] : DEFAULT_ON_EVENTS.has(k));

  return (
    <Card className="p-5 rounded-sm bg-card border-border" data-testid="whatsapp-staff-prefs">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <div className="text-sm font-bold text-slate-100 mb-1 flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-emerald-400" />
            WhatsApp Bildirim Tercihleri
          </div>
          <div className="text-xs text-slate-500">
            ACUTO 360 işleri ve süre uyarılarını WhatsApp'tan alın. Numara profilinizden
            gelir — değiştirmek için yöneticinizden isteyin.
          </div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer shrink-0" title={disabledByPhone ? "Telefon numarası tanımlı değil" : ""}>
          <input
            data-testid="whatsapp-prefs-master"
            type="checkbox"
            checked={masterOn}
            disabled={saving || disabledByPhone}
            onChange={(e) => persist({ enabled: e.target.checked })}
            className="w-4 h-4"
          />
          <span className="text-xs uppercase font-mono tracking-widest text-slate-300">
            {masterOn ? "Açık" : "Kapalı"}
          </span>
        </label>
      </div>

      <div className="text-xs text-slate-400 mb-4 flex items-center gap-2 flex-wrap">
        <span className="uppercase font-mono tracking-widest text-slate-500">Telefon:</span>
        {state.has_phone ? (
          <span className="font-mono text-slate-200" data-testid="whatsapp-prefs-phone">{state.phone}</span>
        ) : (
          <span className="text-amber-400 text-[11px]" data-testid="whatsapp-prefs-no-phone">
            Kayıtlı telefon yok — WhatsApp seçenekleri kapalı görünür.
          </span>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-2 opacity-100" style={{ opacity: disabledByPhone ? 0.55 : 1 }}>
        {catalog.map((c) => {
          const on = isOn(c.key);
          return (
            <label
              key={c.key}
              data-testid={`whatsapp-event-${c.key}`}
              className={`flex items-center gap-3 p-2.5 rounded-sm border cursor-pointer transition-colors ${
                on
                  ? "border-emerald-900/60 bg-emerald-950/20 text-slate-100"
                  : "border-slate-800 bg-slate-900/40 text-slate-500"
              }`}
            >
              <input
                type="checkbox"
                checked={on}
                disabled={saving || disabledByPhone}
                onChange={(e) => persist({ events: { [c.key]: e.target.checked } })}
                className="w-4 h-4"
              />
              <span className="text-xs flex-1">{c.label}</span>
              <span className={`text-[10px] uppercase font-mono tracking-widest ${on ? "text-emerald-400" : "text-slate-600"}`}>
                {on ? "Açık" : "Kapalı"}
              </span>
            </label>
          );
        })}
      </div>

      {state.last_error && (
        <div className="mt-3 text-[11px] text-rose-400" data-testid="whatsapp-prefs-last-error">
          Son gönderim hatası: {state.last_error}
        </div>
      )}
    </Card>
  );
}

// ---- DocumentTemplatesTab (Belge Şablonu Tasarımcısı) ---------------------
const SYSTEM_TITLES = { order: "Sipariş Formu", quote: "Teklif", invoice: "Fatura", proforma: "Proforma Fatura", production: "Üretim Formu" };
const COLOR_PRESETS = ["#2563eb", "#0ea5e9", "#10b981", "#8b5cf6", "#ef4444", "#0f172a"];

function DocumentTemplatesTab({ canEdit = true }) {
  const [templates, setTemplates] = useState([]);
  const [activeType, setActiveType] = useState("order");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newFormOpen, setNewFormOpen] = useState(false);
  const [newFormTitle, setNewFormTitle] = useState("");
  const [newFormCopy, setNewFormCopy] = useState("order");
  const [creating, setCreating] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [dirty, setDirty] = useState({}); // doc_type → kaydedilmemiş değişiklikler
  const fileInputRef = useRef(null);
  const activeDirty = dirty[activeType] && Object.keys(dirty[activeType]).length > 0;

  const templatesMap = useMemo(() => {
    const m = {};
    templates.forEach((t) => { m[t.doc_type] = t; });
    return m;
  }, [templates]);
  const current = templatesMap[activeType] || {};

  useEffect(() => {
    setTitleDraft(current.title || SYSTEM_TITLES[activeType] || activeType);
  }, [activeType, current.title]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/document-templates");
      setTemplates(data.templates || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Değişiklik yalnız taslağa yazılır; kalıcı kayıt "Değişiklikleri Kaydet" ile
  const patch = (updates) => {
    setTemplates((prev) => prev.map((t) => t.doc_type === activeType ? { ...t, ...updates } : t));
    setDirty((prev) => ({ ...prev, [activeType]: { ...(prev[activeType] || {}), ...updates } }));
  };

  const saveChanges = async () => {
    const updates = dirty[activeType];
    if (!updates || saving) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/document-templates/${activeType}`, updates);
      setTemplates((prev) => prev.map((t) => t.doc_type === activeType ? data : t));
      setDirty((prev) => { const n = { ...prev }; delete n[activeType]; return n; });
      toast.success(`${data.title || SYSTEM_TITLES[activeType] || activeType} kaydedildi`);
    } catch (e) {
      toast.error(`Kaydedilemedi: ${formatApiError(e)}`);
    } finally { setSaving(false); }
  };

  const switchType = (t) => {
    if (t !== activeType && activeDirty && !window.confirm("Kaydedilmemiş değişiklikler var. Kaydetmeden geçilsin mi? (değişiklikler bu oturumda taslak olarak kalır)")) return;
    setActiveType(t);
  };

  const saveTitle = () => {
    const trimmed = titleDraft.trim();
    if (!trimmed || trimmed === current.title) return;
    patch({ title: trimmed });
  };

  const createNewForm = async () => {
    if (!newFormTitle.trim()) { toast.error("Başlık boş olamaz"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/document-templates", {
        title: newFormTitle.trim(),
        copy_from: newFormCopy,
      });
      setTemplates((prev) => [...prev, data]);
      setActiveType(data.doc_type);
      setNewFormOpen(false);
      setNewFormTitle("");
      toast.success(`${data.title} eklendi`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreating(false); }
  };

  const deleteCurrent = async () => {
    if (current.is_system) return;
    if (!window.confirm(`"${current.title}" şablonu silinsin mi?`)) return;
    try {
      await api.delete(`/document-templates/${activeType}`);
      setTemplates((prev) => prev.filter((t) => t.doc_type !== activeType));
      setActiveType("order");
      toast.success("Şablon silindi");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) { toast.error("Logo dosyası 1MB'den küçük olmalı"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/document-templates/${activeType}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setTemplates((prev) => prev.map((t) => t.doc_type === activeType ? { ...t, logo_url: data.logo_url } : t));
      toast.success("Logo yüklendi");
    } catch (err) { toast.error(formatApiError(err)); }
    finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    if (!window.confirm("Logo kaldırılsın mı?")) return;
    try {
      await api.delete(`/document-templates/${activeType}/logo`);
      setTemplates((prev) => prev.map((t) => t.doc_type === activeType ? { ...t, logo_url: "" } : t));
      toast.success("Logo kaldırıldı");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) {
    return <div className="text-slate-500 text-sm p-6"><Loader2 className="w-4 h-4 inline mr-2 animate-spin" /> Yükleniyor…</div>;
  }

  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="document-templates-tab">
      {/* SOL: Ayarlar — settings.edit yoksa salt-okunur (fieldset disabled) */}
      <fieldset disabled={!canEdit} className="space-y-4 border-0 p-0 m-0 min-w-0 disabled:opacity-60" data-testid="tpl-settings-fieldset">
        <Card className="p-4 rounded-sm bg-card border-border">
          <div className="flex items-center gap-1 border-b border-slate-800 pb-3 mb-3 flex-wrap">
            {templates.map((t) => (
              <button
                key={t.doc_type}
                data-testid={`tpl-type-${t.doc_type}`}
                onClick={() => switchType(t.doc_type)}
                className={`px-3 py-1.5 rounded-sm text-xs uppercase tracking-widest font-mono transition-colors flex items-center gap-1.5 ${
                  activeType === t.doc_type
                    ? "bg-blue-600 text-white"
                    : "text-slate-400 hover:text-slate-100 hover:bg-slate-900"
                }`}
              >
                {t.title || SYSTEM_TITLES[t.doc_type] || t.doc_type}
                {!t.is_system && (
                  <span className="text-[8px] px-1 py-0.5 bg-emerald-500/20 text-emerald-300 rounded-sm">özel</span>
                )}
                {dirty[t.doc_type] && <span className="w-1.5 h-1.5 rounded-full bg-amber-400" title="Kaydedilmemiş değişiklik" />}
              </button>
            ))}
            <button
              data-testid="tpl-new-form-btn"
              onClick={() => setNewFormOpen(true)}
              className="px-3 py-1.5 rounded-sm text-xs uppercase tracking-widest font-mono border border-dashed border-slate-700 text-slate-400 hover:text-blue-400 hover:border-blue-800 hover:bg-blue-950/20 transition-colors inline-flex items-center gap-1"
            >
              <Plus className="w-3 h-3" /> Yeni Form
            </button>
            {saving && <Loader2 className="w-3.5 h-3.5 ml-auto animate-spin text-blue-400" />}
          </div>

          <div className={`flex items-center justify-between gap-2 mb-4 p-2 rounded-sm border ${activeDirty ? "border-amber-800 bg-amber-950/30" : "border-slate-800"}`} data-testid="tpl-save-bar">
            <span className={`text-[11px] font-mono ${activeDirty ? "text-amber-300" : "text-slate-500"}`} data-testid="tpl-dirty-state">
              {activeDirty ? "Kaydedilmemiş değişiklikler var" : "Tüm değişiklikler kaydedildi"}
            </span>
            <Button size="sm" onClick={saveChanges} disabled={!activeDirty || saving}
              className="rounded-sm text-xs uppercase bg-blue-600 hover:bg-blue-500 text-white" data-testid="tpl-save-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Değişiklikleri Kaydet
            </Button>
          </div>

          {/* Başlık düzenleme */}
          <div className="mb-4">
            <Label className="text-[10px] uppercase font-mono tracking-widest text-slate-500">Belge Başlığı</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                data-testid="tpl-title"
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={saveTitle}
                onKeyDown={(e) => { if (e.key === "Enter") saveTitle(); }}
                className="rounded-sm text-sm"
                placeholder="Belge başlığı (PDF'te büyük yazıyla görünür)"
              />
              {!current.is_system && (
                <Button
                  data-testid="tpl-delete-btn"
                  onClick={deleteCurrent}
                  variant="ghost"
                  size="sm"
                  className="text-rose-400 hover:text-rose-300 hover:bg-rose-950/40 rounded-sm text-xs"
                  title="Şablonu Sil"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
            <div className="text-[10px] text-slate-500 mt-1 font-mono">
              Kod: <span className="text-slate-400">{activeType}</span>
              {current.is_system && <span className="text-slate-600"> · sistem şablonu (silinemez)</span>}
            </div>
          </div>

          {/* Logo section */}
          <div className="mb-4">
            <div className="text-xs uppercase font-mono tracking-widest text-slate-500 mb-2 flex items-center justify-between">
              <span><ImagePlus className="w-3.5 h-3.5 inline mr-1" /> Logo</span>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="tpl-show-logo"
                  checked={!!current.show_logo}
                  onChange={(e) => patch({ show_logo: e.target.checked })}
                  className="w-4 h-4"
                />
                <span className="text-[10px]">Göster</span>
              </label>
            </div>
            <div className="flex items-center gap-3">
              {current.logo_url ? (
                <img
                  src={current.logo_url}
                  alt="Logo"
                  className="h-14 w-auto max-w-[200px] object-contain bg-white/5 border border-slate-800 rounded-sm p-1"
                  data-testid="tpl-logo-preview"
                />
              ) : (
                <div className="h-14 w-32 border border-dashed border-slate-700 rounded-sm flex items-center justify-center text-slate-600 text-xs">
                  Logo yok
                </div>
              )}
              <div className="flex flex-col gap-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  onChange={uploadLogo}
                  className="hidden"
                  data-testid="tpl-logo-file"
                />
                <Button
                  size="sm"
                  variant="outline"
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                  className="rounded-sm text-xs uppercase"
                  data-testid="tpl-logo-upload-btn"
                >
                  {uploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
                  {current.logo_url ? "Değiştir" : "Yükle"}
                </Button>
                {current.logo_url && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={removeLogo}
                    className="rounded-sm text-xs uppercase text-rose-400 hover:text-rose-300"
                    data-testid="tpl-logo-remove-btn"
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> Kaldır
                  </Button>
                )}
              </div>
            </div>
            <div className="text-[10px] text-slate-500 mt-2">PNG/JPG/SVG · Maks 1MB</div>
          </div>

          {/* Cari / Firma bilgileri */}
          <div className="mb-4 p-3 border border-slate-800 rounded-sm bg-slate-950/40">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase font-mono tracking-widest text-slate-500">
                Cari Bilgileri (Logo Altında)
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="tpl-toggle-show_company_info"
                  checked={!!current.show_company_info}
                  onChange={(e) => patch({ show_company_info: e.target.checked })}
                  className="w-4 h-4"
                />
                <span className="text-[10px]">Göster</span>
              </label>
            </div>
            <div className="grid grid-cols-1 gap-2">
              {[
                { key: "company_name", label: "Firma Ünvanı", placeholder: "FUCAN PERSONEL GİYİM SAN. TİC. LTD. ŞTİ." },
                { key: "company_address", label: "Adres", placeholder: "Örn: Organize San. Böl. Cd. No:12 Bursa" },
                { key: "company_tax_office", label: "Vergi Dairesi", placeholder: "Örn: Bursa" },
                { key: "company_tax_no", label: "Vergi No / VKN", placeholder: "1234567890" },
                { key: "company_phone", label: "Telefon", placeholder: "+90 224 000 00 00" },
                { key: "company_email", label: "E-posta", placeholder: "info@fucan.com.tr" },
                { key: "company_web", label: "Web Sitesi", placeholder: "www.fucan.com.tr" },
                { key: "company_iban", label: "IBAN", placeholder: "TR00 0000 0000 0000 0000 0000 00" },
              ].map((f) => (
                <div key={f.key}>
                  <Label className="text-[10px] text-slate-500">{f.label}</Label>
                  <Input
                    data-testid={`tpl-${f.key}`}
                    value={current[f.key] || ""}
                    onChange={(e) => setTemplates((prev) => prev.map((t) => t.doc_type === activeType ? { ...t, [f.key]: e.target.value } : t))}
                    onBlur={(e) => patch({ [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    className="rounded-sm mt-0.5 h-8 text-xs"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Toggles — Satır bazlı sütun kontrolleri */}
          <div className="mb-3">
            <div className="text-[10px] uppercase font-mono tracking-widest text-slate-500 mb-2">
              Satır Sütunları — Görünsün / Görünmesin
            </div>
            <div className="space-y-2">
              {[
                { key: "show_description", label: "Ürün Açıklaması (Beden/Renk/Not)" },
                { key: "show_product_image", label: "Ürün Resmi (A)" },
                { key: "show_artwork_image", label: "Baskı / Nakış Önizleme (B)" },
                { key: "show_product_code", label: "Ürün Kodu / SKU" },
                { key: "show_barcode", label: "Barkod" },
                { key: "show_shelf_location", label: "Raf Yeri / Depo" },
                { key: "show_unit_price", label: "Birim Fiyat" },
                { key: "show_unit_price_excl_vat", label: "Birim Fiyat (KDV Hariç)" },
                { key: "show_discount", label: "İndirim Oranı" },
                { key: "show_discounted_price", label: "İndirimli Birim Fiyat" },
                { key: "show_tax", label: "KDV Oranı" },
                { key: "show_subtotal_excl_vat", label: "KDV Hariç Satır Toplamı" },
                { key: "show_line_total", label: "Satır Toplam Tutar" },
                { key: "show_notes", label: "Satır Not Alanı (Açıklama içinde)" },
                { key: "show_signature_line", label: "İmza Alanı" },
              ].map((f) => (
                <label
                  key={f.key}
                  data-testid={`tpl-toggle-${f.key}`}
                  className="flex items-center justify-between p-2 rounded-sm border border-slate-800 hover:border-slate-700 cursor-pointer"
                >
                  <span className="text-xs text-slate-200">{f.label}</span>
                  <input
                    type="checkbox"
                    checked={!!current[f.key]}
                    onChange={(e) => patch({ [f.key]: e.target.checked })}
                    className="w-4 h-4"
                  />
                </label>
              ))}
            </div>
          </div>

          {/* Text customizations */}
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase font-mono tracking-widest text-slate-500">Başlık Metni</Label>
              <Input
                data-testid="tpl-header-text"
                value={current.header_text || ""}
                onChange={(e) => setTemplates((prev) => prev.map((t) => t.doc_type === activeType ? { ...t, header_text: e.target.value } : t))}
                onBlur={(e) => patch({ header_text: e.target.value })}
                placeholder="FUCAN – PERSONEL GİYİM SAN. TİC. LTD. ŞTİ."
                className="rounded-sm mt-1 text-sm"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-mono tracking-widest text-slate-500">Alt Bilgi (Footer)</Label>
              <Input
                data-testid="tpl-footer-text"
                value={current.footer_text || ""}
                onChange={(e) => setTemplates((prev) => prev.map((t) => t.doc_type === activeType ? { ...t, footer_text: e.target.value } : t))}
                onBlur={(e) => patch({ footer_text: e.target.value })}
                className="rounded-sm mt-1 text-sm"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-mono tracking-widest text-slate-500">İmza Etiketi</Label>
              <Input
                data-testid="tpl-signature-label"
                value={current.signature_label || ""}
                onChange={(e) => setTemplates((prev) => prev.map((t) => t.doc_type === activeType ? { ...t, signature_label: e.target.value } : t))}
                onBlur={(e) => patch({ signature_label: e.target.value })}
                placeholder="Yetkili İmza"
                className="rounded-sm mt-1 text-sm"
              />
            </div>
          </div>
        </Card>

        <Card className="p-4 rounded-sm bg-card border-border">
          <div className="text-xs uppercase font-mono tracking-widest text-slate-500 mb-3">
            <Palette className="w-3.5 h-3.5 inline mr-1" /> Renk & Görünüm
          </div>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase font-mono tracking-widest text-slate-500">Vurgu Rengi</Label>
              <div className="flex items-center gap-2 mt-1">
                {COLOR_PRESETS.map((c) => (
                  <button
                    key={c}
                    data-testid={`tpl-color-${c.replace('#','')}`}
                    onClick={() => patch({ primary_color: c })}
                    className={`w-7 h-7 rounded-sm border-2 transition-all ${
                      current.primary_color === c ? "border-white scale-110" : "border-slate-800"
                    }`}
                    style={{ backgroundColor: c }}
                    title={c}
                  />
                ))}
                <Input
                  data-testid="tpl-color-input"
                  type="color"
                  value={current.primary_color || "#2563eb"}
                  onChange={(e) => patch({ primary_color: e.target.value })}
                  className="w-12 h-8 rounded-sm p-1 cursor-pointer"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase font-mono tracking-widest text-slate-500">Para Birimi</Label>
                <Input
                  data-testid="tpl-currency-symbol"
                  value={current.currency_symbol || "₺"}
                  onChange={(e) => setTemplates((prev) => prev.map((t) => t.doc_type === activeType ? { ...t, currency_symbol: e.target.value } : t))}
                  onBlur={(e) => patch({ currency_symbol: e.target.value })}
                  className="rounded-sm mt-1 h-8 text-sm"
                  maxLength={5}
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase font-mono tracking-widest text-slate-500">Kağıt</Label>
                <select
                  data-testid="tpl-paper-size"
                  value={current.paper_size || "A4"}
                  onChange={(e) => patch({ paper_size: e.target.value })}
                  className="w-full mt-1 h-8 bg-background border border-input rounded-sm px-2 text-sm text-slate-100"
                >
                  <option value="A4">A4</option>
                  <option value="A5">A5</option>
                </select>
              </div>
            </div>
          </div>
        </Card>
      </fieldset>

      {/* SAĞ: Canlı Önizleme */}
      <Card className="p-6 rounded-sm bg-white border-slate-300 text-slate-900 sticky top-4 self-start" data-testid="tpl-preview">
        <div className="text-[10px] uppercase font-mono tracking-widest text-slate-500 mb-3 -mt-2">
          Önizleme · {current.title || SYSTEM_TITLES[activeType] || activeType}
        </div>
        <TemplatePreview t={current} type={activeType} />
      </Card>

      {/* Yeni Form Dialog */}
      <Dialog open={newFormOpen} onOpenChange={setNewFormOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="tpl-new-form-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-blue-400" /> Yeni Belge Şablonu
            </DialogTitle>
            <DialogDescription className="text-xs">
              Yeni bir form (örn. İrsaliye, Depo Çıkış, Fiyat Teklifi) oluşturun. Aynı
              düzeni kullanır — başlık ve tüm ayarları özelleştirebilirsiniz.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Belge Başlığı</Label>
              <Input
                data-testid="tpl-new-form-title"
                value={newFormTitle}
                onChange={(e) => setNewFormTitle(e.target.value)}
                placeholder="Örn: İrsaliye"
                className="rounded-sm mt-1"
                autoFocus
                onKeyDown={(e) => { if (e.key === "Enter" && !creating) createNewForm(); }}
              />
            </div>
            <div>
              <Label className="text-xs">Bir Şablonu Baz Al</Label>
              <select
                data-testid="tpl-new-form-copy"
                value={newFormCopy}
                onChange={(e) => setNewFormCopy(e.target.value)}
                className="w-full mt-1 h-9 bg-background border border-input rounded-sm px-2 text-sm text-slate-100"
              >
                {templates.map((t) => (
                  <option key={t.doc_type} value={t.doc_type}>
                    {t.title || SYSTEM_TITLES[t.doc_type] || t.doc_type}
                  </option>
                ))}
              </select>
              <div className="text-[10px] text-slate-500 mt-1">
                Seçilen şablonun tüm ayarları (logo, renk, sütunlar) yeni forma kopyalanır.
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setNewFormOpen(false)} disabled={creating} className="rounded-sm">
              Vazgeç
            </Button>
            <Button
              data-testid="tpl-new-form-create"
              onClick={createNewForm}
              disabled={creating || !newFormTitle.trim()}
              className="rounded-sm bg-blue-600 hover:bg-blue-500"
            >
              {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
              Oluştur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TemplatePreview({ t, type }) {
  const primary = t.primary_color || "#2563eb";
  const curr = t.currency_symbol || "₺";
  const docTitle = (t.title || { order: "SİPARİŞ", quote: "TEKLİF", invoice: "FATURA" }[type] || type).toLocaleUpperCase("tr-TR");
  const items = [
    { name: "COOLTEE MİKA POLO", code: "PL-001", barcode: "8690000000012", shelf: "R-01", desc: "Beden: L · Renk: Lacivert", qty: 20, price: 250, disc: 5, tax: 20 },
    { name: "İş Tişörtü Klasik", code: "TS-002", barcode: "8690000000029", shelf: "R-04", desc: "Beden: M · Renk: Beyaz", qty: 15, price: 120, disc: 0, tax: 20 },
  ];
  const _fmt = (n) => n.toLocaleString("tr-TR", { minimumFractionDigits: 2 });
  const money = (n) => t.currency_position === "after" ? `${_fmt(n)} ${curr}` : `${curr} ${_fmt(n)}`;
  const compute = (it) => {
    const gross = it.qty * it.price;
    const discVal = gross * (it.disc / 100);
    const subNoVat = gross - discVal;
    const total = subNoVat * (1 + it.tax / 100);
    const discUnit = it.price * (1 - it.disc / 100);
    return { gross, subNoVat, total, discUnit };
  };
  const totalSum = items.reduce((s, it) => s + compute(it).total, 0);
  const subtotalNoVat = items.reduce((s, it) => s + compute(it).subNoVat, 0);

  return (
    <div className="text-xs">
      {/* Header */}
      <div className="flex items-start justify-between pb-3 border-b-2" style={{ borderColor: primary }}>
        <div className="min-w-0 flex-1">
          {t.show_logo && t.logo_url ? (
            <img src={t.logo_url} alt="Logo" className="h-10 max-w-[140px] object-contain mb-1.5" />
          ) : null}
          {t.show_company_info !== false && (
            <div className="text-[10px] leading-4 text-slate-700">
              {t.company_name && <div className="font-bold text-slate-900 text-[11px]">{t.company_name}</div>}
              {t.company_address && <div className="text-slate-600">{t.company_address}</div>}
              {(t.company_tax_office || t.company_tax_no) && (
                <div className="text-slate-600">
                  {t.company_tax_office ? `V.D.: ${t.company_tax_office}` : ""}
                  {t.company_tax_office && t.company_tax_no ? " · " : ""}
                  {t.company_tax_no ? `VKN: ${t.company_tax_no}` : ""}
                </div>
              )}
              {(t.company_phone || t.company_email) && (
                <div className="text-slate-600">
                  {t.company_phone ? `Tel: ${t.company_phone}` : ""}
                  {t.company_phone && t.company_email ? " · " : ""}
                  {t.company_email}
                </div>
              )}
              {t.company_web && <div className="text-slate-600">{t.company_web}</div>}
              {t.company_iban && <div className="text-slate-500">IBAN: {t.company_iban}</div>}
            </div>
          )}
        </div>
        <div className="text-right shrink-0 ml-3">
          <div className="text-2xl font-bold" style={{ color: primary }}>{docTitle}</div>
          <div className="text-[10px] text-slate-500">No: <b>SO-2026-XXXX</b></div>
        </div>
      </div>

      {/* Customer + meta */}
      <div className="grid grid-cols-2 gap-4 my-3">
        <div>
          <div className="text-[9px] uppercase text-slate-500 font-mono">Müşteri</div>
          <div className="font-semibold">Örnek Müşteri A.Ş.</div>
          <div className="text-[10px] text-slate-500">Ahmet Yılmaz · +90 212 555 0000</div>
        </div>
        <div>
          <div className="text-[9px] uppercase text-slate-500 font-mono">Bilgi</div>
          <div className="text-[10px] text-slate-600">Tarih: 27.07.2026</div>
          <div className="text-[10px] text-slate-600">Termin: 15.08.2026</div>
        </div>
      </div>

      {/* Table — dynamic columns */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse min-w-[600px]">
          <thead>
            <tr className="text-[8px] uppercase" style={{ background: "#0f172a", color: "white" }}>
              <th className="px-1 py-1 text-left">#</th>
              {t.show_product_image && <th className="px-1 py-1 text-left">Görsel</th>}
              {t.show_artwork_image && <th className="px-1 py-1 text-left">Baskı</th>}
              <th className="px-1 py-1 text-left">Ürün</th>
              {t.show_product_code && <th className="px-1 py-1 text-left">Kod</th>}
              {t.show_barcode && <th className="px-1 py-1 text-left">Barkod</th>}
              {t.show_shelf_location && <th className="px-1 py-1 text-left">Raf</th>}
              <th className="px-1 py-1 text-right">Adet</th>
              {t.show_unit_price && <th className="px-1 py-1 text-right">Birim {curr}</th>}
              {t.show_unit_price_excl_vat && <th className="px-1 py-1 text-right">B. (KDV-)</th>}
              {t.show_discount && <th className="px-1 py-1 text-right">İsk.</th>}
              {t.show_discounted_price && <th className="px-1 py-1 text-right">İnd.B.</th>}
              {t.show_tax && <th className="px-1 py-1 text-right">KDV</th>}
              {t.show_subtotal_excl_vat && <th className="px-1 py-1 text-right">KDV-Top</th>}
              {t.show_line_total && <th className="px-1 py-1 text-right">Toplam</th>}
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => {
              const c = compute(it);
              return (
                <tr key={i} className="border-b border-slate-200 text-[9px]">
                  <td className="px-1 py-1 text-center">{i + 1}</td>
                  {t.show_product_image && (
                    <td className="px-1 py-1">
                      <div className="w-8 h-8 bg-slate-100 border border-slate-300 rounded-sm flex items-center justify-center text-slate-400 text-[8px]">img</div>
                    </td>
                  )}
                  {t.show_artwork_image && (
                    <td className="px-1 py-1">
                      <div className="w-8 h-8 bg-blue-50 border border-blue-200 rounded-sm flex items-center justify-center text-blue-500 text-[8px]" title="Baskı önizleme">baskı</div>
                    </td>
                  )}
                  <td className="px-1 py-1">
                    <div className="font-semibold">{it.name}</div>
                    {t.show_description && (
                      <div className="text-[8px] text-slate-500">{it.desc}</div>
                    )}
                  </td>
                  {t.show_product_code && <td className="px-1 py-1 font-mono text-[8px]">{it.code}</td>}
                  {t.show_barcode && <td className="px-1 py-1 font-mono text-[8px]">{it.barcode}</td>}
                  {t.show_shelf_location && <td className="px-1 py-1">{it.shelf}</td>}
                  <td className="px-1 py-1 text-right tabular-nums">{it.qty}</td>
                  {t.show_unit_price && <td className="px-1 py-1 text-right tabular-nums">{money(it.price)}</td>}
                  {t.show_unit_price_excl_vat && <td className="px-1 py-1 text-right tabular-nums">{money(it.price)}</td>}
                  {t.show_discount && <td className="px-1 py-1 text-right">{it.disc ? `%${it.disc}` : "—"}</td>}
                  {t.show_discounted_price && <td className="px-1 py-1 text-right tabular-nums">{money(c.discUnit)}</td>}
                  {t.show_tax && <td className="px-1 py-1 text-right">%{it.tax}</td>}
                  {t.show_subtotal_excl_vat && <td className="px-1 py-1 text-right tabular-nums">{money(c.subNoVat)}</td>}
                  {t.show_line_total && <td className="px-1 py-1 text-right tabular-nums font-semibold">{money(c.total)}</td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="ml-auto w-[220px] mt-3 text-[10px]">
        <div className="flex justify-between py-0.5">
          <span className="text-slate-500">Ara Toplam</span><span className="tabular-nums">{money(subtotalNoVat)}</span>
        </div>
        <div className="flex justify-between py-0.5">
          <span className="text-slate-500">KDV (%20)</span><span className="tabular-nums">{money(totalSum - subtotalNoVat)}</span>
        </div>
        <div className="flex justify-between py-1.5 border-t-2 font-bold text-sm mt-1" style={{ borderColor: "#0f172a" }}>
          <span>GENEL TOPLAM</span>
          <span className="tabular-nums" style={{ color: primary }}>{money(totalSum)}</span>
        </div>
      </div>

      {/* Footer */}
      {t.footer_text && (
        <div className="text-[9px] text-slate-500 mt-4 pt-3 border-t border-slate-200">
          {t.footer_text}
        </div>
      )}
      {t.show_signature_line && (
        <div className="grid grid-cols-2 gap-6 mt-6 text-[9px] text-slate-500">
          <div>
            Hazırlayan
            <div className="border-t border-slate-400 mt-6 pt-1 text-slate-400">_________________________</div>
          </div>
          <div>
            {t.signature_label || "Yetkili İmza"}
            <div className="border-t border-slate-400 mt-6 pt-1 text-slate-400">_________________________</div>
          </div>
        </div>
      )}
    </div>
  );
}

